/* eslint-disable ember/no-classic-components */
import Component from "@ember/component";
import { hash } from "@ember/helper";
import { action, computed } from "@ember/object";
import { dependentKeyCompat } from "@ember/object/compat";
import { service } from "@ember/service";
import { tagName } from "@ember-decorators/component";
import BreadCrumbs from "discourse/components/bread-crumbs";
import BulkSelectToggle from "discourse/components/bulk-select-toggle";
import CategoryNotificationsTracking from "discourse/components/category-notifications-tracking";
import CreateTopicButton from "discourse/components/create-topic-button";
import DButton from "discourse/components/d-button";
import NavigationBar from "discourse/components/navigation-bar";
import PluginOutlet from "discourse/components/plugin-outlet";
import TagInfoButton from "discourse/components/tag-info-button";
import TagNotificationsTracking from "discourse/components/tag-notifications-tracking";
import TopicDismissButtons from "discourse/components/topic-dismiss-buttons";
import lazyHash from "discourse/helpers/lazy-hash";
import { filterTypeForMode } from "discourse/lib/filter-mode";
import { NotificationLevels } from "discourse/lib/notification-levels";
import { applyValueTransformer } from "discourse/lib/transformer";
import NavItem from "discourse/models/nav-item";
import CategoriesAdminDropdown from "discourse/select-kit/components/categories-admin-dropdown";
import TagCategoryAdminDropdown from "discourse/select-kit/components/tag-category-admin-dropdown";
import { and, gt } from "discourse/truth-helpers";

@tagName("")
export default class DNavigation extends Component {
  @service router;
  @service site;
  @service siteSettings;
  @service currentUser;

  @computed("siteSettings.fixed_category_positions")
  get fixedCategoryPositions() {
    return this.siteSettings.fixed_category_positions;
  }

  get createTopicLabel() {
    const defaultKey = "topic.create";

    return applyValueTransformer(
      "create-topic-label",
      this.site.desktopView ? defaultKey : "",
      { site: this.site, defaultKey }
    );
  }

  get showBulkSelectInNavControls() {
    const enableOnDesktop = applyValueTransformer(
      "bulk-select-in-nav-controls",
      false,
      { site: this.site }
    );

    return (
      (this.site.mobileView || enableOnDesktop) &&
      this.notCategoriesRoute &&
      this.canBulkSelect
    );
  }

  @dependentKeyCompat
  get filterType() {
    return filterTypeForMode(this.filterMode);
  }

  // Should be a `readOnly` instead but some themes/plugins still pass
  // the `categories` property into this component
  @computed()
  get categories() {
    let categories = this.site.categoriesList;

    if (!this.siteSettings.allow_uncategorized_topics) {
      categories = categories.filter(
        (category) => category.id !== this.site.uncategorized_category_id
      );
    }

    if (this.currentUser?.indirectly_muted_category_ids) {
      categories = categories.filter(
        (category) =>
          !this.currentUser.indirectly_muted_category_ids.includes(category.id)
      );
    }

    return categories;
  }

  @computed("category")
  get showCategoryNotifications() {
    return this.category && this.currentUser;
  }

  @computed("category.notification_level")
  get categoryNotificationLevel() {
    if (
      this.currentUser?.indirectly_muted_category_ids?.includes(
        this.category.id
      )
    ) {
      return NotificationLevels.MUTED;
    } else {
      return this.category?.notification_level;
    }
  }

  // don't show tag notification menu on tag intersections
  @computed("tagNotification", "additionalTags")
  get showTagNotifications() {
    return this.tagNotification && !this.additionalTags;
  }

  @computed("category", "createTopicDisabled")
  get categoryReadOnlyBanner() {
    if (this.category && this.currentUser && this.createTopicDisabled) {
      return this.category.read_only_banner;
    }
  }

  @computed("category.can_edit")
  get showCategoryEdit() {
    return this.category?.can_edit;
  }

  @computed("tag", "tag.name", "additionalTags", "currentUser.canEditTags")
  get showTagEdit() {
    return (
      this.tag &&
      this.tag.name !== "none" &&
      !this.additionalTags &&
      this.currentUser?.canEditTags
    );
  }

  @computed(
    "category.can_edit",
    "tag",
    "tag.name",
    "additionalTags",
    "currentUser.canEditTags"
  )
  get showCombinedAdminDropdown() {
    return this.category?.can_edit && this.showTagEdit;
  }

  @computed(
    "filterType",
    "category",
    "noSubcategories",
    "tag",
    "router.currentRoute.queryParams",
    "skipCategoriesNavItem"
  )
  get navItems() {
    const items = NavItem.buildList(this.category, {
      filterType: this.filterType,
      noSubcategories: this.noSubcategories,
      currentRouteQueryParams: this.router?.currentRoute?.queryParams,
      tag: this.tag,
      siteSettings: this.siteSettings,
      skipCategoriesNavItem: this.skipCategoriesNavItem,
    });

    return applyValueTransformer("navigation-items", items, {
      category: this.category,
      tag: this.tag,
      filterType: this.filterType,
    });
  }

  @computed("filterType")
  get notCategoriesRoute() {
    return this.filterType !== "categories";
  }

  @action
  async changeTagNotificationLevel(notificationLevel) {
    const response = await this.tagNotification.update({
      notification_level: notificationLevel,
    });

    const payload = response.responseJson;

    this.tagNotification.set("notification_level", notificationLevel);

    this.currentUser.setProperties({
      watched_tags: payload.watched_tags,
      watching_first_post_tags: payload.watching_first_post_tags,
      tracked_tags: payload.tracked_tags,
      muted_tags: payload.muted_tags,
      regular_tags: payload.regular_tags,
    });
  }

  @action
  changeCategoryNotificationLevel(notificationLevel) {
    this.category.setNotification(notificationLevel);
  }

  @action
  selectCategoryAdminDropdownAction(actionId) {
    switch (actionId) {
      case "create":
        this.createCategory();
        break;
      case "reorder":
        this.reorderCategories();
        break;
    }
  }

  @action
  handleTagCategoryAdmin(actionId) {
    switch (actionId) {
      case "editCategory":
        this.editCategory();
        break;
      case "editTag":
        this.router.transitionTo(
          "tag.edit.tab",
          this.tag.slug,
          this.tag.id,
          "general"
        );
        break;
    }
  }

  @action
  clickCreateTopicButton() {
    this.createTopic();
  }

  <template>
    <BreadCrumbs
      @categories={{this.categories}}
      @category={{this.category}}
      @noSubcategories={{this.noSubcategories}}
      @tag={{this.tag}}
      @additionalTags={{this.additionalTags}}
    />

    <PluginOutlet
      @name="after-breadcrumbs"
      @outletArgs={{lazyHash
        categories=this.categories
        category=this.category
        tag=this.tag
        additionalTags=this.additionalTags
      }}
    />

    {{#unless this.additionalTags}}
      {{! nav bar doesn't work with tag intersections }}
      <NavigationBar
        @navItems={{this.navItems}}
        @filterMode={{this.filterMode}}
        @category={{this.category}}
        @tag={{this.tag}}
      />
    {{/unless}}

    <div class="navigation-controls">
      {{#if this.showBulkSelectInNavControls}}
        <BulkSelectToggle @bulkSelectHelper={{@bulkSelectHelper}} />
      {{/if}}

      <TopicDismissButtons
        @position="top"
        @selectedTopics={{@bulkSelectHelper.selected}}
        @model={{@model}}
        @showResetNew={{@showResetNew}}
        @showDismissRead={{@showDismissRead}}
        @resetNew={{@resetNew}}
        @dismissRead={{@dismissRead}}
      />

      {{#if this.showCategoryAdmin}}
        {{#if this.fixedCategoryPositions}}
          <CategoriesAdminDropdown
            @onChange={{this.selectCategoryAdminDropdownAction}}
            @options={{hash triggerOnChangeOnTab=false}}
          />
        {{else}}
          <DButton
            @action={{this.createCategory}}
            @icon="plus"
            @label={{if
              this.site.mobileView
              "categories.category"
              "category.create"
            }}
            class="btn-default"
            id="create-category"
          />
        {{/if}}
      {{/if}}

      {{#if this.showCombinedAdminDropdown}}
        <TagCategoryAdminDropdown
          @category={{this.category}}
          @tag={{this.tag}}
          @onChange={{this.handleTagCategoryAdmin}}
          @options={{hash triggerOnChangeOnTab=false}}
        />
      {{else}}
        {{#if (and this.category this.showCategoryEdit)}}
          <DButton
            @action={{this.editCategory}}
            @icon="wrench"
            @title="category.edit_title"
            class="btn-default edit-category"
          />
        {{/if}}

        {{#if this.showTagEdit}}
          <TagInfoButton @tag={{this.tag}} @currentUser={{this.currentUser}} />
        {{/if}}
      {{/if}}

      <PluginOutlet
        @name="before-create-topic-button"
        @outletArgs={{lazyHash
          canCreateTopic=this.canCreateTopic
          createTopicDisabled=this.createTopicDisabled
          createTopicLabel=this.createTopicLabel
          additionalTags=this.additionalTags
          category=this.category
          tag=this.tag
        }}
      />

      <CreateTopicButton
        @canCreateTopic={{this.canCreateTopic}}
        @action={{this.clickCreateTopicButton}}
        @label={{this.createTopicLabel}}
        @btnTypeClass={{if
          this.siteSettings.modernize_foundation_theme
          "btn-primary"
        }}
        @showDrafts={{if (gt this.draftCount 0) true false}}
      />

      <PluginOutlet
        @name="after-create-topic-button"
        @outletArgs={{lazyHash
          canCreateTopic=this.canCreateTopic
          createTopicDisabled=this.createTopicDisabled
          createTopicLabel=this.createTopicLabel
          category=this.category
          tag=this.tag
        }}
      />

      {{#if this.category}}
        {{#unless this.tag}}
          {{! don't show category notification menu on tag pages }}
          {{#if this.showCategoryNotifications}}
            {{#unless this.category.deleted}}
              <CategoryNotificationsTracking
                @levelId={{this.categoryNotificationLevel}}
                @showFullTitle={{false}}
                @showCaret={{false}}
                @onChange={{this.changeCategoryNotificationLevel}}
              />
            {{/unless}}
          {{/if}}
        {{/unless}}
      {{/if}}

      {{#if this.tag}}
        {{#unless this.category}}
          {{! don't show tag notification menu on category pages }}
          {{#if this.showTagNotifications}}
            <TagNotificationsTracking
              @onChange={{this.changeTagNotificationLevel}}
              @levelId={{this.tagNotification.notification_level}}
            />
          {{/if}}
        {{/unless}}
      {{/if}}
    </div>
  </template>
}
